import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  getEntityLocationName,
  LOCATION_NAME_REQUIRED_MESSAGE,
} from '@/lib/festival-location';
import { toEpochMs } from '@/lib/schedule-datetime';
import {
  DateFormWidget,
  FileUpload,
  FormButtons,
  FormRow,
  LocationMapField,
} from '../controls';
import { ENTITY_MAP_DEFAULT_ZOOM } from '../controls/interactive-map/constants';

function buildDefaultValues(attraction) {
  return {
    attractionName: attraction.attractionName || '',
    attractionDescription: attraction.attractionDescription || '',
  };
}

export function AttractionForm({
  attraction,
  onDateChange,
  handleSave,
  onLocationChange,
  onLocationNameChange,
  onFileUpload,
  onFileDelete,
}) {
  const [locationNameError, setLocationNameError] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(attraction),
    mode: 'onSubmit',
  });

  const attractionId = attraction?.attractionId;

  useEffect(() => {
    reset(buildDefaultValues(attraction));
  }, [attractionId, reset]);

  const dates = [
    {
      startTime: attraction.startTime ?? null,
      endTime: attraction.endTime ?? null,
      startTimeMS: toEpochMs(attraction.startTimeMS, attraction.startTime),
      endTimeMS: toEpochMs(attraction.endTimeMS, attraction.endTime),
      selectedDate: attraction.selectedDate ?? null,
      dateInput: attraction.dateInput ?? null,
    },
  ];

  const fileList = [];

  if (attraction?.photoURL) {
    fileList.push({
      uid: '-1',
      name: attraction.attractionPhoto,
      url: attraction.photoURL,
      status: 'done',
    });
  }

  const submit = (values) => {
    if (!getEntityLocationName(attraction)) {
      setLocationNameError(LOCATION_NAME_REQUIRED_MESSAGE);
      return;
    }
    setLocationNameError(null);
    handleSave(values);
  };

  const handleLocationChange = (location, displayName) => {
    if (displayName?.trim()) {
      setLocationNameError(null);
    }
    onLocationChange?.(location, displayName);
  };

  const handleLocationNameChange = (nextLocationName) => {
    if (nextLocationName?.trim()) {
      setLocationNameError(null);
    }
    onLocationNameChange?.(nextLocationName);
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="flex">
        <div className="w-3/4 p-4">
          <FormRow
            label="Attraction Name"
            error={errors.attractionName?.message}
          >
            <input
              type="text"
              placeholder="Name"
              {...register('attractionName', {
                required: 'Attraction name is required',
              })}
            />
          </FormRow>
          <DateFormWidget
            label="Dates & Times"
            dates={dates}
            handleDateChange={onDateChange}
          />
        </div>
        <div className="w-1/4 p-4">
          <div className="w-full">
            <FileUpload
              fileList={fileList}
              onFileUpload={onFileUpload}
              onFileDelete={onFileDelete}
            />
          </div>
        </div>
      </div>
      <FormRow error={locationNameError}>
        <LocationMapField
          key={attractionId}
          shouldPromptForMap
          defaultZoom={ENTITY_MAP_DEFAULT_ZOOM}
          location={attraction.location}
          locationName={attraction.location?.locationName}
          onLocationChange={handleLocationChange}
          onLocationNameChange={handleLocationNameChange}
        />
      </FormRow>
      <FormRow label="Description">
        <textarea
          placeholder="Attraction Description..."
          {...register('attractionDescription')}
        />
      </FormRow>
      <FormButtons title="Attraction" />
    </form>
  );
}
